import React, { useEffect, useMemo, useState } from "react";
import {
    Area,
    Bar,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useSelector } from "react-redux";
import { useDemandInventoryLineGraphData, usePotentialOverstockLineGraphData } from "../../services/redux/hooks/useInventory";
import { IDemandPurchase, IInventoryTrend, ILTTrend, InventoryGraphPayload, IPotentialStockOutInventoryTrend } from "../../services/interface/IAlerts";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { formatNumber, formatter } from "../../utils/formatter";
import { IRoot } from "../../services/interface/IHome";

const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
        <ul className="flex items-center gap-2">
            {payload.map((entry: any, index: number) => (
                <li key={`item-${index}`} className="flex items-center mb-1">
                    <svg width="30" height="8">
                        <line x1="0" y1="4" x2="10" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                        <polygon points="12,1 16,4 12,7 8,4" fill={entry?.color} />
                        <line x1="15" y1="4" x2="25" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                    </svg>
                    <span style={{ color: entry.color }} className="ml-2 font-semibold chart-legend">
                        {entry.value === "onHand" ? "On Hand (Qty.)" :
                            entry.value === "onhand" ? "On Hand (Qty.)" :   
                                entry.value === "replt" ? "Lead Time (Weeks)" :
                                    entry.value === "effectivess" ? "Safety Stock (Qty.)" :
                                        entry.value === "adjusted" ? "Safety Stock (Qty.) + Cycle Stock (Qty.)" : entry.value}
                    </span>
                </li>
            ))}
        </ul>
    );
};

const DemandPurchaseLegend = (props: any) => {
    const { payload } = props;
    return (
        <ul className="flex items-center gap-2">
            {payload?.filter((entry: any) => entry.dataKey !== "PrevForecast").map((entry: any, index: number) => (
                <li key={`item-${index}`} className="flex items-center mb-1">
                    <svg width="30" height="8">
                        <line x1="0" y1="4" x2="10" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                        <polygon points="12,1 16,4 12,7 8,4" fill={entry?.color} />
                        <line x1="15" y1="4" x2="25" y2="4" stroke={entry?.color} strokeWidth="1.5" />
                    </svg>
                    <span style={{ color: entry.color }} className="ml-2 font-semibold font-[0.6vw]">
                        {entry.value}
                    </span>
                </li>
            ))}
        </ul>
    )
}


const LineGraph = (props: any) => {
    const location = useLocation();
    const alertType = new URLSearchParams(location.search).get("alert");
    const updatedAlert = String(alertType)?.charAt(0).toLowerCase() + alertType?.slice(1) || "";
    const selectedGBPFilter = useSelector((state: any) => state.filter.selectedGBPFilter);
    const [isActive, setIsActive] = useState<number>(0);
    const [invGraphPayload, setInvGraphPayload] = useState<InventoryGraphPayload>({
        alertType: updatedAlert,
        clustername: [],
        futureSegmentation: [],
        l2: [],
        l3: [],
        material: [],
        materialDescription: [],
        mrpType: [],
        plant: [],
        vendorName: [],
    });
    const [invGraphData, setInvGraphData] = useState<any[]>([]);
    const [invDemandPurchaseData, setInvDemanPurchaseData] = useState<IDemandPurchase | any>();
    const [invTrendGraphData, setInvTrendGraphData] = useState<IInventoryTrend[]>();
    const [invLTTrendGraphData, setInvLTTrendGraphData] = useState<ILTTrend[]>();
    const applyFilter = useSelector((state:IRoot)=>state.filter.applyFilter);

    const { mutate: mutateInvData, data: inventoryLineGraphData } = useDemandInventoryLineGraphData();

    useEffect(() => {
        if(applyFilter.status || applyFilter.rowNo===-1){
            const payload = {
                ...invGraphPayload,
                ...selectedGBPFilter
            }
            mutateInvData(payload);
        }
    }, [applyFilter]);

    useEffect(() => {
        let finalData: any = [];
        if (inventoryLineGraphData) {
            let demand = inventoryLineGraphData?.demand;
            let data = inventoryLineGraphData?.potentialOverstock;
            let inventory = inventoryLineGraphData?.inventory;
            let replt = inventoryLineGraphData?.repData;
            data.forEach((element: any, index: number) => {
                let obj = {
                  monthStartDate: moment(element?.monthstartdate?.slice(0, 10)).format("MMM'YY"),
                  adjusted: element?.adjusted,
                  effectivess: element?.effectivess,
                  onHand: index === data.length - 1 ? element?.netinv : element?.onhand
                };
                finalData.push(obj);
            });
            setInvGraphData(finalData);

            let demandforecast: any = [];
            demand.forEach((element: any, index: number) => {
                let obj = {
                    dayDate: moment(element?.dayDate?.slice(0, 10)).format("MMM'YY"),
                    // Demand: index < 3 ? element?.demand : null,
                    Demand: element?.demand,
                    // Forecast: index >= 3 ? element?.forecast : null,
                    Forecast: element?.forecast,
                    // Purchase: index < 3 ? element?.purchases : null,
                    Purchases: element?.purchases,
                    // PrevForecast: index === 2 ? element?.demand : (index === 3 ? element?.forecast : null) // Connect Demand and Forecast with a dotted line
                };
                demandforecast.push(obj);
            });
            setInvDemanPurchaseData(demandforecast);

            let inventoryTrend: IInventoryTrend[] = [];
            inventory.forEach((element: IInventoryTrend, index: number) => {
                let obj = {
                    monthdate: moment(element?.monthdate?.slice(0, 10)).format("MMM'YY"),
                    onhand: element?.onhand
                }
                inventoryTrend.push(obj);
            });
            setInvTrendGraphData(inventoryTrend);

            let ltTrend: ILTTrend[] = [];
            replt?.forEach((element: ILTTrend, index: number) => {
                let obj = {
                    monthdate: moment(element?.monthdate?.slice(0, 10)).format("MMM'YY"),
                    replt: element?.replt
                }
                ltTrend.push(obj);
            });
            setInvLTTrendGraphData(ltTrend);
        }
    }, [inventoryLineGraphData]);

    return (
        <div className="w-[100%] h-[100%] bg-[#FFF] shadow rounded px-[2%] py-[12px] border-[1px] border-[#BEBEBE] flex flex-col">
            <div className="flex justify-between items-center mb-[0.5vw]">
                <p className="text-mainGreen font-semibold chart-title self-start">
                    {props.flag == 1 && <span className="pb-1">{props.title}</span>}
                    {props.flag == 2 && isActive === 0 && "Purchases vs Demand (Historical and Forecasted)"}
                    {props.flag == 2 && isActive === 1 && "Inventory Trend (Qty)"}
                    {props.flag == 2 && isActive === 2 && "LT Trend"}
                </p>
                <div>
                    {props.flag == 2 &&
                        <div className="flex gap-[2px]">
                            <div
                                onClick={() => setIsActive(0)}
                                className={`${isActive == 0 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                rounded-tl-md rounded-bl-md p-[2px] pb-[3px] pr-[3px] flex h-[18px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 0 ? "chart-btn text-white font-semibold" : "chart-btn font-semibold"}>
                                    Purchases/Forecast Demand
                                </span>
                            </div>

                            <div
                                onClick={() => setIsActive(1)}
                                className={`${isActive == 1 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                p-[2px] pb-[3px] pr-[3px] flex h-[18px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 1 ? "chart-btn text-white font-semibold" : "chart-btn font-semibold"}>
                                    Inventory Trend
                                </span>
                            </div>

                            <div
                                onClick={() => setIsActive(2)}
                                className={`${isActive == 2 ? "bg-deloitteGreen" : "bg-[#949494]"} 
                                rounded-tr-md rounded-br-md p-[2px] pb-[3px] pr-[3px] flex h-[18px] items-center text-white cursor-pointer`}
                            >
                                <span className={isActive == 2 ? "chart-btn text-white font-semibold" : "chart-btn font-semibold"}>
                                    LT Trend
                                </span>
                            </div>
                        </div>
                    }
                </div>
            </div>
            <div className="flex h-[30vh]">
                {props.flag == 1 && 
                    <div className="flex-grow h-full">
                        <ResponsiveContainer width={"100%"} height={"100%"}>
                            <ComposedChart data={invGraphData} margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                                <XAxis stroke="#949494" tickFormatter={(tick, index) => {
                                    if (index === invGraphData.length - 1) {
                                        return "Inv at end of LT";
                                    }
                                    else{
                                        return tick;
                                    }
                                }}
                                dataKey="monthStartDate" fontSize={"0.8vw"} padding={{ left: 0, right: 30 }} />
                                <YAxis interval="equidistantPreserveStart" tickFormatter={formatter} stroke="#949494" fontSize={"0.8vw"} tickCount={10} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    content={<CustomLegend />}
                                    align="left"
                                    verticalAlign="top"
                                    iconSize={8}
                                    wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                                />
                                <Area type="monotone" dataKey="effectivess" stroke="#012169" fillOpacity="0.2"
                                activeDot={{stroke:'#012169', fill: 'white', strokeWidth:2, r: 4}}  />
                                <Area type="monotone" dataKey="adjusted" stroke="#62B5E5" fillOpacity="0.2" 
                                activeDot={{stroke:'#62B5E5', fill: 'white', strokeWidth:2, r: 4}}  />
                                <Line type="monotone" dataKey="onHand" stroke="#86BC25" 
                                strokeWidth="2"
                                dot={{ fill: "#86BC25", r: 2 }} 
                                activeDot={{stroke:'#86BC25', fill: 'white', strokeWidth:2, r: 4}} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                }
                {props.flag == 2 && isActive == 0 && 
                    <div className="flex-grow h-full">
                        <ResponsiveContainer width={"100%"} height={"100%"}>
                            <ComposedChart data={invDemandPurchaseData} margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                                <XAxis stroke="#949494" dataKey="dayDate" fontSize={"0.8vw"} padding={{ left: 0, right: 30 }} />
                                <YAxis interval="equidistantPreserveStart" tickFormatter={formatter} stroke="#949494" fontSize={"0.8vw"} tickCount={10} />
                                <Tooltip content={<DemandPurchaseTooltip />} />
                                <Legend
                                    content={<DemandPurchaseLegend />}
                                    align="left"
                                    verticalAlign="top"
                                    iconSize={8}
                                    wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                                />
                                <Bar dataKey="Demand" fill="#949494" maxBarSize={30}></Bar>
                                <Bar dataKey="Forecast" fill="#86BC25" maxBarSize={30}></Bar>
                                <Line type="linear" dataKey="Purchases" stroke="#0097A9" strokeWidth="2" dot={{ fill: "#0097A9", r: 2 }} activeDot={{stroke:'#0097A9', fill: 'white', strokeWidth:2, r: 4}} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                }
                {props.flag == 2 && isActive == 1 &&
                    <div className="flex-grow h-full">
                        <ResponsiveContainer width={"100%"} height={"100%"}>
                            <ComposedChart data={invTrendGraphData} margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                                <XAxis stroke="#949494" dataKey="monthdate" fontSize={"0.8vw"} padding={{ left: 0, right: 30 }} />
                                <YAxis interval="equidistantPreserveStart" tickFormatter={formatter} stroke="#949494" fontSize={"0.8vw"} tickCount={10} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    content={<CustomLegend />}
                                    align="left"
                                    verticalAlign="top"
                                    iconSize={8}
                                    wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                                />
                                <Line type="linear" dataKey="onhand" stroke="#0097A9" 
                                strokeWidth="2"
                                dot={{ fill: "#0097A9", r: 2 }}
                                activeDot={{stroke:'#0097A9', fill: 'white', strokeWidth:2, r: 4}}  />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                }
                {props.flag == 2 && isActive == 2 &&
                    <div className="flex-grow h-full">
                        <ResponsiveContainer width={"100%"} height={"100%"}>
                            <ComposedChart data={invLTTrendGraphData} margin={{ top: 20, right: 20, bottom: 0, left: -10 }}>
                                <XAxis stroke="#949494" dataKey="monthdate" fontSize={"0.8vw"} padding={{ left: 0, right: 30 }} />
                                <YAxis interval="equidistantPreserveStart" stroke="#949494" fontSize={"0.8vw"} tickCount={10} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    content={<CustomLegend />}
                                    align="left"
                                    verticalAlign="top"
                                    iconSize={8}
                                    wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                                />
                                <Line type="linear" dataKey="replt" stroke="#0097A9" 
                                strokeWidth="2"
                                dot={{ fill: "#0097A9", r: 2 }}
                                activeDot={{stroke:'#0097A9', fill: 'white', strokeWidth:2, r: 4}}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                }
            </div>
        </div>
    );
}

const CustomTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <p className="text-black text-fs16 font-bold mb-3">
                    {payload[0]?.payload?.monthStartDate ||
                        payload[0]?.payload?.monthdate}
                </p>
                <div className="flex flex-col gap-2">
                    {payload?.map(($: any) => (
                        <div className="flex flex-col" key={$?.name}>
                            <p className="text-black text-fs14">
                                {($.name === "onHand") ? "On Hand (Qty.)" :
                                    $.name === "onhand" ? "On Hand (Qty.)" :
                                        $.name === "replt" ? "Lead Time (Weeks)" :
                                            $.name === "effectivess" ? "Safety Stock (Qty.)" :
                                                $.name === "adjusted" ? "Safety Stock (Qty.) + Cycle Stock (Qty.)" : $.name}
                            </p>
                            <p style={{ color: $.color }} className="text-black text-fs20 font-bold">
                                {($.name === "onHand") ? `${formatter($.value)}` :
                                        $.name === "onhand" ? `${formatter($.value)}` :
                                            $.name === "replt" ? Number($.value).toFixed(1) :
                                                $.name === "effectivess" ? `${formatter($.value)}` :
                                                    $.name === "adjusted" ? `${formatter($.value)}` : $.value}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const DemandPurchaseTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <p className="text-black text-fs16 font-bold mb-3">
                    {payload[0]?.payload?.dayDate}
                </p>
                <div className="flex flex-row gap-2">
                    {payload?.filter(($: any) => $.name !== "PrevForecast").map(($: any) => (
                        <div className="flex flex-col" key={$?.name}>
                            <p className="text-black text-fs14">
                                {$?.name}
                            </p>
                            <p style={{ color: $.color }} className="text-black text-fs20 font-bold">
                                {formatter($?.value)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

export default LineGraph;
